<script setup>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import {
  ref, computed, onMounted, watch, nextTick, provide,
} from 'vue';
import { usePresetSnapshot } from '@/composables/usePresetSnapshot';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import KeyVal from '@/components/Apps/KeyValInput.vue';
import HelpTip from '@/components/HelpTip.vue';

const $toast = useToast();

const store = useStore();
const currentProject = computed(() => store.state.projects.currentProject);

const emit = defineEmits(['job_started']);

const props = defineProps({
  appName: {
    type: String,
    required: true,
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
});

const appStartModal = ref(null);
const schemaGroupRef = ref(null);
const schema = ref(null);
const is_local = ref(false);
const appDetails = ref(null);
const env = ref({});
const description = ref('');
const message = ref('');
const componentKey = ref(0);
const dataConnect = new DatastoreConnect();
const configGroupValues = ref({});
const hasSchemaData = ref(false);
const jobName = ref('');
const selectedSnapshotId = ref(null);
const selectedSnapshotName = ref('');

const { presetSnapshotId: sharedPresetSnapshotId } = usePresetSnapshot();
provide('presetSnapshotId', selectedSnapshotId);

var appTaskId=0;

function showModal(snapshotId = null, snapshotName = '') {
  // Store snapshot context for use in pre-selecting schema fields
  selectedSnapshotId.value = snapshotId !== null ? Number(snapshotId) : null;
  selectedSnapshotName.value = snapshotName || '';
  // Write to shared composable so SchemaSnapshot can read it at any nesting depth
  sharedPresetSnapshotId.value = selectedSnapshotId.value;
  window.__appStartPresetSnapshotId = selectedSnapshotId.value;

  // Generate job name with format: appname_date using CURRENT appName
  // This ensures we always use the latest app name, not a stale one
  if (props.appName) {
    const date = new Date().toISOString().slice(0, 10); // e.g., 2026-04-01
    jobName.value = `${props.appName}_${date}`;
  }

  // Re-apply defaultValues so callers can set them just before showModal()
  configGroupValues.value = { ...props.defaultValues };
  // Force SchemaGroup to remount so it picks up the new configGroupValues
  componentKey.value += 1;

  appStartModal.value.showModal();
}

function closeModal() {
  appStartModal.value.closeModal();
  sharedPresetSnapshotId.value = null;
  window.__appStartPresetSnapshotId = null;
  // Note: jobName is reset after API call completes
}

defineExpose({
  showModal,
  closeModal,
  selectedSnapshotId,
  selectedSnapshotName,
});

watch(() => props.appName, () => {
  // Reset when switching to a different app
  resetData();
  getAppDetails();
  
  // Pre-generate the job name for the new app
  if (props.appName) {
    const date = new Date().toISOString().slice(0, 10);
    jobName.value = `${props.appName}_${date}`;
  }
});

onMounted(async () => {
  getAppDetails();
});

function resetData() {
  schema.value = null;
  env.value = {};
  // Don't reset jobName here - it will be set when showModal() is called
  configGroupValues.value = { ...props.defaultValues };
}

function jobStartHandler() {
  // Validate Job Name
  if (!jobName.value || jobName.value.trim() === '') {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Required field missing: Job Name',
      type: 'error',
      position: 'top',
    });
    return;
  }
  
  // Validate required fields
  if (schemaGroupRef.value) {
    const missingFields = schemaGroupRef.value.validateRequiredFields();
    if (missingFields.length > 0) {
      const fieldList = missingFields.join(', ');
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Required field${missingFields.length > 1 ? 's' : ''} missing: ${fieldList}`,
        type: 'error',
        position: 'top',
      });
      return;
    }
  }
  
  // Capture jobName before closing modal
  const jobNameToSubmit = jobName.value;
  
  closeModal();

  const environ = { ...env.value };
  environ["HOST"] = window.location.hostname;
  environ["TOKEN"] = store.state.user.token;
 
  const schemaData = configGroupValues.value;

  dataConnect.api('job.run', { name: appDetails.value.name, env: environ, data: schemaData, job_name: jobNameToSubmit })
    .then((ret) => {
      if (ret.result) {
        const $toast = useToast();
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Job Started, ID:${ret.result.job_id}`,
          type: 'success',
          position: 'top',
        });

        emit('job_started', ret.result.job_id);
      } else if (ret.error) {
        const $toast = useToast();
        $toast.open({
          queue: true,
          duration: 5000,
          message: ret.error.message,
          type: 'error',
          position: 'top',
        });
      }
    })
    .catch((error) => {
      console.log(error);
      const $toast = useToast();
      $toast.open({
        queue: true,
        duration: 5000,
        message: error,
        type: 'error',
        position: 'top',
      });
    })
    .finally(() => {
      // Reset job name after API call completes (success or error)
      jobName.value = '';
    });
}

async function getAppDetails() {
  if (props.appName === '') return;

  hasSchemaData.value = false;
  dataConnect.api("app.get", { "name": props.appName })
    .then((data) => {
      if (data.result) {
        schema.value = data.result.schema;
        appDetails.value = data.result;
        description.value = data.result.description;
        for (const e of schema.value) {
          if (e.type === 'env') {
            env.value = e.values;
          } else if (e.type === 'selection') {

          } else {
            hasSchemaData.value = true;
          }
        }
      } else if (data.error) {
        console.log(data.error);
      }
    }).catch((err) => {
      console.log(err);
    });
}
</script>

<template>
  <Modal
    ref="appStartModal"
    :title="'Start App'"
  >
    <!-- <template #modal-header /> -->
    <template #modal-main>
      <div class="settings scrollbar">
        <div class="container">
          <div>
            <div class="static-card">
              <div class="header-row">
                <h2>Description</h2>
                <HelpTip>
                  This app will trigger background processes which may incur costs.
                </HelpTip>
              </div>
              <div class="description-text">
                {{ description }}
              </div>

              <h2 class="mt-1">Job Name</h2>
              <input 
                v-model="jobName" 
                type="text" 
                class="job-name-input"
                placeholder="Enter job name"
                required
              />
            </div>

            <div v-if="Object.keys(env).length>0" class="static-card">
              <h2>Environment Variables</h2>
              <KeyVal
                v-model:data="env"
              />
            </div>
          </div>

          <div v-if="hasSchemaData" class="dynamic-card">
            <h2>App Settings</h2>
            <div class="container pt-2">
              <SchemaGroup
                ref="schemaGroupRef"
                :key="componentKey"
                v-model:params="configGroupValues"
                :fields="schema"
                :defaultValues="defaultValues"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <div v-if="message" class="result error">
          <span>{{ message }}</span>
        </div>
        <button
          class="button modal-action-button"

          @click="jobStartHandler"
        >
          Start App
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
// $cardMargin: 8px;
$cardPadding: 16px;

.settings {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  background: var(--color-white-600);

  span {
    width: 100%;
    font-size: 1rem;
    font-weight: 700;
    text-align: left;
    @include themify() {
      color: themed('color-primary');
    }
  }
}
.container {
  padding: 0 16px;
  gap: 16px;
}

h2 {
    color: #250E81;
  }

.header-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h2 {
    margin: 0;
  }
}

.description-text {
  font-size: 13px;
  font-style: italic;
  margin-top: 4px;
  margin-bottom: 4px;
  line-height: 1.4;
}

.job-name-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-primary-200);
  border-radius: 4px;
  font-size: 14px;
  background: var(--color-white-100);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
  }
  
  &::placeholder {
    color: var(--color-grey-400);
  }
}

.snapshot-context {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.82rem;
  @include themify() {
    background: rgba(37, 14, 129, 0.06);
    border: 1px solid rgba(37, 14, 129, 0.15);
  }

  &__label {
    font-weight: 600;
    @include themify() {
      color: #555;
    }
  }

  &__id {
    font-weight: 700;
    font-family: monospace;
    @include themify() {
      color: #250E81;
    }
  }

  &__name {
    @include themify() {
      color: #333;
    }
  }
}

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  margin-bottom: 16px;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.dynamic-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

:deep(.dynamic-card > .container > .model-settings > .field-group:not(:first-of-type)) {
  padding-top: 24px;
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

}

.footer {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
  border-top: solid 3px var(--gray-300);
  background: var(--color-white-100);

  button {
    margin-left: auto;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error') !important;
  }
}

.radio-group {
  display: inline-block;
  margin: 20px;
  border-radius: 5px;
  overflow: hidden;
  @include themify() {
    border: solid 1px themed('color-primary');
  }
}

.radio-group:focus-within, .radio-group:active {
  @include themify() {
    box-shadow:0 0 5px themed('color-primary');
  }
}

.radio-group input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.radio-group input[type="radio"] + label {
  min-width: 6rem;
  max-width: 50%;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.25;
  padding: 5px 20px;
  transition: all 0.4s ease;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25) inset;
  @include themify() {
    color: themed('color-primary');
    background: themed('color-white-100');
  }
}

.radio-group input[type="radio"]:not(:disabled):hover + label {
  @include themify() {
    background: themed('color-primary-200');
    color: themed('primary-text-color');
  }
}

.radio-group input[type="radio"]:checked + label,
.radio-group input[type="radio"]:checked:hover + label {
  opacity: 1;
  z-index: 1;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.25), 0 0 6px 2px rgba(0, 0, 0, 0.25);
  @include themify() {
    background: themed('color-primary');
    color: themed('primary-text-color');
  }
}

.radio-group.full-width-selector {
  display: flex;
  width: 100%;
  margin: 0;
}

.radio-group.full-width-selector input[type="radio"] + label {
  min-width: 1px;
  padding: 3px 10px;
  flex: 1 1 auto;
}

.dataset-selector{
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;

  :first-child {
    flex: 1 1 40%;
  }

  :not(:first-child) {
    flex: 1 1 60%;
  }
}

.trainer-session-row-switch {
  flex-direction: row !important;
  overflow: visible;
  align-items: center;

  label:first-child {
    flex: 1 1 40%;
    max-width: 40%;
  }
}

.full-width {
  width: 100%;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

.slider-settings {
  display: flex;
  flex-direction: row;
  padding: 12px;
  border-radius: inherit;
  gap: 6px;
  min-width: 350px;
  min-height: 50px;
  justify-content: center;
  align-items: center;
  overflow: auto;

  &__row {
    display: flex;
    align-items: center;
  }

  & input[type=checkbox] {
    margin-right: 12px;
  }

  & span {
    width: 40px;
    text-align: center;
  }
}

._train{
    border: 2px solid rgb(44, 145, 246);
}

._validation{
  @include themify {
    border: 2px solid themed('range-slider-selected-color');
  }
}

._test{
  @include themify {
    border: 2px solid themed('range-slider-selected-color-right');
  }
}

.clickable {
  cursor: pointer;
}

.split-radio[type="radio"] {
  height: 18px;
  width: 18px;
  @include themify() {
    accent-color: themed('color-primary');
  }
}

:deep() {
  .v-label{
    opacity: 1 !important;
  }
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px !important;
  font-weight: 600!important;
  letter-spacing: 0 !important;
  @include themify() {
    color: themed('form-label-color') !important;
  }
}

</style>
